import { persistedFindingAssetIds } from './findingOwnership';
import { directoryAssetIdentity } from './directoryAssetIdentity';

function text(value) {
  return value == null ? '' : String(value).trim();
}

function lower(value) {
  return text(value).toLowerCase();
}

function arrayValues(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
}

function findingId(finding) {
  return text(finding?.id ?? finding?.findingId ?? finding?.persistedFindingId);
}

function assetFindingIds(asset) {
  const direct = [
    ...arrayValues(asset?.findingIds),
    ...arrayValues(asset?.linkedFindingIds),
    ...arrayValues(asset?.relatedFindingIds),
  ];
  const nested = arrayValues(asset?.findings)
    .map(f => findingId(f))
    .filter(Boolean);
  return new Set([...direct, ...nested].map(lower).filter(Boolean));
}

function assetIds(asset) {
  return new Set([
    asset?.assetId,
    asset?.id,
    asset?.externalId,
  ].map(lower).filter(Boolean));
}

function findingAssetIds(finding) {
  return new Set([
    finding?.assetId,
    finding?.affectedAssetId,
    finding?.asset_id,
    finding?.affected_asset_id,
    finding?.detail?.assetId,
    finding?.detail?.affectedAssetId,
    finding?.additionalData?.assetId,
    finding?.additionalData?.affectedAssetId,
    ...arrayValues(finding?.assetIds),
    ...arrayValues(finding?.affectedAssetIds),
    ...arrayValues(finding?.detail?.assetIds),
    ...arrayValues(finding?.detail?.affectedAssetIds),
    ...arrayValues(finding?.additionalData?.assetIds),
    ...arrayValues(finding?.additionalData?.affectedAssetIds),
  ].map(lower).filter(Boolean));
}

function hasOverlap(left, right) {
  for (const value of left) {
    if (right.has(value)) return true;
  }
  return false;
}

function inferredAssetMatch(asset, finding) {
  const name = lower(asset?.displayName);
  const hostname = lower(asset?.hostname);
  const ip = lower(asset?.ipAddress);
  const id = lower(asset?.assetId);
  const findingAsset = lower(finding?.asset ?? finding?.affectedAsset ?? finding?.affectedComponent);

  return (name && findingAsset.includes(name)) ||
    (hostname && findingAsset.includes(hostname)) ||
    (ip && findingAsset.includes(ip)) ||
    (id && findingAsset === id);
}

export function assetFindingLink(asset, finding) {
  if (!asset || !finding) return null;

  if (directoryAssetIdentity(asset)) {
    // A backend finding ID alone may have come from an older name-based API query.
    // Canonical objects need the persisted ownership array, not inferred target aliases.
    const canonicalIds = new Set([asset?.assetId ?? asset?.id].map(lower).filter(Boolean));
    const persistedIds = persistedFindingAssetIds(finding);
    const explicitIds = new Set(persistedIds ?? []);
    const id = lower(findingId(finding));
    const summaries = [...arrayValues(asset?.findings), ...arrayValues(asset?.relatedFindings)];
    const summaryOwns = summaries.some(summary =>
      id && lower(findingId(summary)) === id &&
      hasOverlap(canonicalIds, new Set(persistedFindingAssetIds(summary) ?? [])));
    // An explicit nonmatching owner on the full finding cannot be overridden by stale summaries.
    if (hasOverlap(canonicalIds, explicitIds) || (persistedIds === null && summaryOwns)) {
      return { finding, linkType: 'authoritative', linkReason: 'Explicit directory asset ownership' };
    }
    return null;
  }

  const ids = assetFindingIds(asset);
  const id = lower(findingId(finding));
  if (ids.size > 0 && id && ids.has(id)) {
    return { finding, linkType: 'authoritative', linkReason: 'Backend finding ID' };
  }

  const assetIdSet = assetIds(asset);
  if (assetIdSet.size > 0 && hasOverlap(assetIdSet, findingAssetIds(finding))) {
    return { finding, linkType: 'authoritative', linkReason: 'Backend asset ID' };
  }

  // Reused names/DNs are not evidence of ownership for canonical directory objects.
  if (!directoryAssetIdentity(asset) && inferredAssetMatch(asset, finding)) {
    return { finding, linkType: 'inferred', linkReason: 'Inferred from displayed asset target' };
  }

  return null;
}

export function linkedFindingsForAsset(asset, findings) {
  if (!asset || !Array.isArray(findings)) {
    return { rows: [], counts: { authoritative: 0, inferred: 0 } };
  }

  const rows = findings
    .map(finding => assetFindingLink(asset, finding))
    .filter(Boolean);

  return {
    rows,
    counts: rows.reduce((acc, row) => {
      acc[row.linkType] = (acc[row.linkType] ?? 0) + 1;
      return acc;
    }, { authoritative: 0, inferred: 0 }),
  };
}
